import * as React from "react"
import { cn } from "@/lib/utils"
import { Card, CardContent } from "@/components/ui/card"

interface ResponsiveTableProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode
}

interface ResponsiveTableHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode
}

interface ResponsiveTableBodyProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode
}

interface ResponsiveTableRowProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode
  mobileCard?: boolean
}

interface ResponsiveTableCellProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode
  label?: string
  className?: string
}

interface ResponsiveTableHeaderCellProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode
  className?: string
}

// Main responsive table wrapper
const ResponsiveTable = React.forwardRef<HTMLDivElement, ResponsiveTableProps>(
  ({ className, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn("w-full", className)}
      {...props}
    >
      {/* Desktop table view */}
      <div className="hidden md:block relative w-full overflow-auto">
        <table className="w-full caption-bottom text-sm">
          {children}
        </table>
      </div>
      
      {/* Mobile card view container */}
      <div className="md:hidden space-y-4">
        {children}
      </div>
    </div>
  )
)
ResponsiveTable.displayName = "ResponsiveTable"

// Table header - hidden on mobile
const ResponsiveTableHeader = React.forwardRef<HTMLTableSectionElement, ResponsiveTableHeaderProps>(
  ({ className, children, ...props }, ref) => (
    <thead 
      ref={ref} 
      className={cn("hidden md:table-header-group [&_tr]:border-b", className)} 
      {...props}
    >
      {children}
    </thead>
  )
)
ResponsiveTableHeader.displayName = "ResponsiveTableHeader"

// Table body wrapper
const ResponsiveTableBody = React.forwardRef<HTMLTableSectionElement, ResponsiveTableBodyProps>(
  ({ className, children, ...props }, ref) => (
    <>
      {/* Desktop table body */}
      <tbody
        ref={ref}
        className={cn("hidden md:table-row-group [&_tr:last-child]:border-0", className)}
        {...props}
      >
        {children}
      </tbody>
      
      {/* Mobile cards container */}
      <div className="md:hidden space-y-4">
        {children}
      </div>
    </>
  )
)
ResponsiveTableBody.displayName = "ResponsiveTableBody"

// Table row - renders as table row on desktop, card on mobile
const ResponsiveTableRow = React.forwardRef<HTMLTableRowElement, ResponsiveTableRowProps>(
  ({ className, children, mobileCard = true, ...props }, ref) => (
    <>
      {/* Desktop table row */}
      <tr
        ref={ref}
        className={cn(
          "hidden md:table-row border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted",
          className
        )}
        {...props}
      >
        {children}
      </tr>
      
      {/* Mobile card */}
      {mobileCard && (
        <Card className="md:hidden">
          <CardContent className="p-4 space-y-3">
            {children}
          </CardContent>
        </Card>
      )}
    </>
  )
)
ResponsiveTableRow.displayName = "ResponsiveTableRow"

// Table cell - renders differently on mobile vs desktop
const ResponsiveTableCell = React.forwardRef<HTMLTableCellElement, ResponsiveTableCellProps>(
  ({ className, children, label, ...props }, ref) => (
    <>
      {/* Desktop table cell */}
      <td
        ref={ref}
        className={cn("hidden md:table-cell p-4 align-middle [&:has([role=checkbox])]:pr-0", className)}
        {...props}
      >
        {children}
      </td>
      
      {/* Mobile card row */}
      <div className="md:hidden flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1 sm:gap-4">
        {label && (
          <div className="text-sm font-medium text-muted-foreground min-w-0 sm:w-1/3">
            {label}
          </div>
        )}
        <div className={cn("flex-1 min-w-0", !label && "sm:ml-auto")}>
          {children}
        </div>
      </div>
    </>
  )
)
ResponsiveTableCell.displayName = "ResponsiveTableCell"

// Table header cell - only shows on desktop
const ResponsiveTableHeaderCell = React.forwardRef<HTMLTableCellElement, ResponsiveTableHeaderCellProps>(
  ({ className, children, ...props }, ref) => (
    <th
      ref={ref}
      className={cn(
        "h-12 px-4 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0",
        className
      )}
      {...props}
    >
      {children}
    </th>
  )
)
ResponsiveTableHeaderCell.displayName = "ResponsiveTableHeaderCell"

export {
  ResponsiveTable,
  ResponsiveTableHeader,
  ResponsiveTableBody,
  ResponsiveTableRow,
  ResponsiveTableCell,
  ResponsiveTableHeaderCell,
}